import classNames from 'classnames'
import { FC, ReactNode } from 'react'

import { Box, BoxProps } from '@mui/material'

import './Ribbon.scss'

export type RibbonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'featured'
  | 'soft-launch'
  | 'new'
  | 'public'
  | 'organization'

export type RibbonSide = 'left' | 'right'

export type RibbonPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export type RibbonSize = 'small' | 'medium' | 'large'

export interface RibbonProps extends Omit<BoxProps, 'variant' | 'position'> {
  children: ReactNode
  variant?: RibbonVariant
  position?: RibbonPosition
  side?: RibbonSide
  size?: RibbonSize
  rotated?: boolean
  className?: string
}

/**
 * Ribbon component for displaying labels, badges, or status indicators
 * Supports various positions, sizes, and color variants
 */
export const Ribbon: FC<RibbonProps> = ({
  children,
  variant = 'default',
  position,
  side = 'left',
  size = 'medium',
  rotated = true,
  className,
  ...boxProps
}) => {
  // Determine position based on side prop if position is not explicitly provided
  const finalPosition = position || `top-${side}`

  const ribbonClasses = classNames(
    'Ribbon',
    `Ribbon--${variant}`,
    `Ribbon--${finalPosition}`,
    `Ribbon--${size}`,
    {
      'Ribbon--rotated': rotated,
      'Ribbon--not-rotated': !rotated,
    },
    className
  )

  return (
    <Box className={ribbonClasses} {...boxProps}>
      {children}
    </Box>
  )
}
